import { ColDef } from 'ag-grid-community';
import { CarFrontFill } from 'react-bootstrap-icons';
import { BaseReport } from './BaseReport';
import { ReportConfig, FilterField } from './types';
import { SubscriptionReport as SubscriptionReportType } from '../Data/ApiTypes';
import { Api } from '../Data/Api';
import { toLocalDateString } from '../Data/Date';
import { ServerTransferSavedReportResponse } from '../shared/api/axios-client';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';

interface SubscriptionFilter {
    page: number;
    pageSize: number;
    facilityId?: number;
    email?: string;
    name?: string;
    from?: string;
    to?: string;
    planName?: string;
    status?: string;
    expireDate?: string;
}

const LicensePlateCell = ({ value }: { value: string }) => {
    if (!value) return <span>-</span>;
    
    const plates = value.split('|').map(p => p.trim()).filter(p => p);
    
    if (plates.length === 0) return <span>-</span>;
    if (plates.length === 1) return <span>{plates[0]}</span>;
    
    const additionalCount = plates.length - 1;
    const tooltipContent = plates.slice(1).join('\n');
    
    return (
        <span>
            {plates[0]}{' '}
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={`tooltip-${value}`}>
                        <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
                            {tooltipContent}
                        </div>
                    </Tooltip>
                }
            >
                <Badge bg="secondary" style={{ cursor: 'pointer' }}>
                    +{additionalCount}
                </Badge>
            </OverlayTrigger>
        </span>
    );
};

const defaultColumns: ColDef<SubscriptionReportType>[] = [
    { field: 'name', headerName: 'Parker Name', hide: false, filter: false },
    { field: 'emailAddress', headerName: 'Email', hide: false, filter: false },
    { field: 'plan', headerName: 'Plan', hide: false, filter: false },
    { field: 'quantity', headerName: 'Quantity', type: 'rightAligned', maxWidth: 150, hide: false, filter: false },
    { 
        field: 'status',
        headerName: 'Status',
        hide: false,
        filter: false,
        valueFormatter: params => params.value || '-'
    },
    {
    field: "start",
    headerName: "Joined On",
    hide: false,
    filter: false,
    valueFormatter: (p) => toLocalDateString(p.value),
    },
    {
    field: "expireDate",
    headerName: "Expiration Date",
    hide: false,
    filter: false,
    valueFormatter: (p) => toLocalDateString(p.value),
    },
    { 
        field: 'allLps', 
        headerName: 'All LPs', 
        hide: false, 
        filter: false,
        cellRenderer: (params: any) => <LicensePlateCell value={params.value} />
    },
    { 
        field: 'linkedLps', 
        headerName: 'Linked LPs', 
        hide: false, 
        filter: false,
        cellRenderer: (params: any) => <LicensePlateCell value={params.value} />
    },
];

const filterFields: FilterField[] = [
    { name: 'email', label: 'Email', type: 'text', placeholder: 'Filter by email' },
    { name: 'name', label: 'Parker Name', type: 'text', placeholder: 'Filter by name' },
    { name: 'planName', label: 'Plan Name', type: 'text', placeholder: 'Filter by plan name' },
    { 
        name: 'status', 
        label: 'Status', 
        type: 'select',
        options: [
            { value: 'Active', label: 'Active' },
            { value: 'Canceled', label: 'Canceled' }
        ]
    }
];

const config: ReportConfig<SubscriptionReportType, SubscriptionFilter> = {
    id: 'subscription',
    title: 'Subscription Report',
    icon: <CarFrontFill className="mx-1" style={{ fontSize: '1.7rem', marginTop:'-10px' }} />,
    description: `<p>This report gives you a consolidated view of every parker's active and historical access periods across your facilities. You'll see which plan each parker is on, when their subscriptions start and end, and how many spots they've purchased.</p>
        <p>Use it to:</p>
        <ul>
            <li>Quickly identify whose subscriptions are about to expire</li>
            <li>Help with occupancy and availability</li>
            <li>Audit historical access for billing or compliance</li>
        </ul>`,
    defaultColumns,
    filterFields,
    defaultFilterForm: {
        from: '',
        to: '',
        planName: '',
        email: '',
        name: '',
        status: '',
        quickDateMode: 'All',
        quickPayoutDateMode: 'All',
        payoutFrom: '',
        payoutTo: '',
        age: ''
    },
    getData: async (filter) => {
        const response = await Api.subscriptionReports(filter);
        if (!response.isOk) throw new Error(response.error);
        return response.value;
    }
};

export function SubscriptionReport({
    facilityId,
    savedReports,
    setSavedReports
}: {
    facilityId?: number;
    savedReports: ServerTransferSavedReportResponse [];
    setSavedReports: React.Dispatch<React.SetStateAction<ServerTransferSavedReportResponse []>>;
}) {
    return (
        <BaseReport
            facilityId={facilityId}
            config={config}
            savedReports={savedReports}
            setSavedReports={setSavedReports}
            storageKey='report_columns_subscription'
        />
    );
}